import { useState } from "react";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { DEFAULT_CONTENT, DEFAULT_PRODUCTS, DEFAULT_REVIEWS, DEFAULT_PROJECTS } from "@/lib/defaults";
import { toast } from "sonner";
import { Save, Lock, RefreshCw } from "lucide-react";
import { TextField, TextArea, ImageField, ListEditor, Section } from "@/components/admin/AdminFields";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel";

export default function Admin() {
  const { content, products, reviews, projects, reload } = useContent();
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("content");

  const [draft, setDraft] = useState(content);
  const [prodDraft, setProdDraft] = useState(products);
  const [revDraft, setRevDraft] = useState(reviews);
  const [projDraft, setProjDraft] = useState(projects);
  const [enquiries, setEnquiries] = useState([]);
  const [saving, setSaving] = useState(false);

  const login = async (e) => {
    e?.preventDefault();
    try {
      const r = await api.get("/enquiries", { headers: { "X-Admin-Token": token } });
      setEnquiries(r.data);
      setAuthed(true);
      setDraft(content);
      setProdDraft(products);
      setRevDraft(reviews);
      setProjDraft(projects);
      toast.success("Authenticated.");
    } catch {
      toast.error("Invalid admin token.");
    }
  };

  const set = (path) => (val) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur[k] = cur[k] ?? {};
        cur = cur[k];
      }
      cur[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft, { headers: { "X-Admin-Token": token } });
      toast.success("Content saved.");
      await reload();
    } catch {
      toast.error("Save failed.");
    } finally { setSaving(false); }
  };

  const saveList = async (endpoint, items, setLocal) => {
    setSaving(true);
    try {
      await api.put(`/${endpoint}`, { items }, { headers: { "X-Admin-Token": token } });
      toast.success(`${endpoint} saved.`);
      await reload();
      setLocal(items);
    } catch {
      toast.error(`${endpoint} save failed.`);
    } finally { setSaving(false); }
  };

  const refreshEnquiries = async () => {
    try {
      const r = await api.get("/enquiries", { headers: { "X-Admin-Token": token } });
      setEnquiries(r.data);
    } catch { toast.error("Failed to refresh."); }
  };

  const deleteEnquiry = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`, { headers: { "X-Admin-Token": token } });
      setEnquiries((e) => e.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch { toast.error("Failed."); }
  };

  const resetSection = (key, defaultVal) => set(key)(defaultVal);

  // ---------------- LOGIN ----------------
  if (!authed) {
    return (
      <div data-testid="admin-page" className="pt-32 pb-24">
        <div className="max-w-md mx-auto px-6">
          <div className="text-center mb-10">
            <Lock className="w-10 h-10 mx-auto text-[#D4AF37]" />
            <h1 className="font-display text-3xl mt-4">Admin Console</h1>
            <p className="text-sm text-[#6B7280] mt-2">Enter the admin token to manage site content.</p>
          </div>
          <form onSubmit={login} className="bg-white border border-[#E5E7EB] p-6 space-y-4">
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="admin-token-input"
              placeholder="Admin token"
              className="w-full bg-white border border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-3 px-4 text-sm" />
            <button type="submit" data-testid="admin-load-button" className="w-full btn-charcoal py-3 text-xs tracking-widest uppercase font-semibold">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  // ---------------- AUTHENTICATED ----------------
  const C = draft;
  return (
    <div data-testid="admin-page" className="pt-28 pb-24 bg-[#FAFAFA] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl">Admin Console</h1>
            <p className="text-sm text-[#6B7280] mt-1">Edit every section, image, and list — changes go live instantly on save.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {["content", "products", "reviews", "projects", "enquiries", "analytics"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border ${tab === t ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E7EB] bg-white hover:border-[#1A1A1A]"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* CONTENT TAB */}
        {tab === "content" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={saveContent} disabled={saving} data-testid="admin-save-content"
                className="btn-gold px-6 py-3 text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save All Content"}
              </button>
            </div>

              <Section title="Site Info (logo, tagline, phones, email, address)" defaultOpen>
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Company Name" value={C.site.name} onChange={set("site.name")} />
                <TextField label="Tagline" value={C.site.tagline} onChange={set("site.tagline")} />
                <TextField label="Phone" value={C.site.phone} onChange={set("site.phone")} />
                <TextField label="Phone (Alt)" value={C.site.phone_alt} onChange={set("site.phone_alt")} />
                <TextField label="WhatsApp (digits only, with country code)" value={C.site.whatsapp} onChange={set("site.whatsapp")} />
                <TextField label="Email" value={C.site.email} onChange={set("site.email")} />
              </div>
              <ImageField label="Logo Image (shown in navbar — for white background; transparent PNG recommended)" value={C.site.logo_image} onChange={set("site.logo_image")} token={token} />
              <ImageField label="Logo Image — Dark version (shown in footer / dark backgrounds; optional)" value={C.site.logo_image_dark} onChange={set("site.logo_image_dark")} token={token} />
              <TextArea label="Primary Address" value={C.site.primary_address} onChange={set("site.primary_address")} rows={2} />
              <TextField label="Maps Search Query" value={C.site.maps_query} onChange={set("site.maps_query")} />
            </Section>

            <Section title="Outlets (multiple addresses)">
              <ListEditor label="Outlets" items={C.outlets || []} onChange={set("outlets")}
                newItem={() => ({ name: "New Outlet", address: "", phone: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Outlet Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
                    <TextArea label="Address" value={it.address} onChange={(v) => upd({ ...it, address: v })} rows={2} />
                    <TextField label="Phone" value={it.phone} onChange={(v) => upd({ ...it, phone: v })} />
                  </div>
                )} />
            </Section>

            <Section title="Hero Section (homepage banner)">
              <TextField label="Eyebrow (small label above headline)" value={C.hero.eyebrow} onChange={set("hero.eyebrow")} />
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Headline line 1" value={C.hero.headline_1} onChange={set("hero.headline_1")} />
                <TextField label="Headline line 2 (before accent)" value={C.hero.headline_2} onChange={set("hero.headline_2")} />
                <TextField label="Accent word (gold italic)" value={C.hero.headline_accent} onChange={set("hero.headline_accent")} />
                <TextField label="Headline line 3 (after accent)" value={C.hero.headline_3} onChange={set("hero.headline_3")} />
              </div>
              <TextArea label="Subheadline" value={C.hero.subheadline} onChange={set("hero.subheadline")} />
              <ImageField label="Hero Background Image" value={C.hero.image} onChange={set("hero.image")} token={token} />
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Primary CTA Label" value={C.hero.cta_primary} onChange={set("hero.cta_primary")} />
                <TextField label="Secondary CTA Label" value={C.hero.cta_secondary} onChange={set("hero.cta_secondary")} />
              </div>
            </Section>

            <Section title="Statistics (animated counters)">
              <ListEditor label="Stats" items={C.stats || []} onChange={set("stats")}
                newItem={() => ({ value: 0, suffix: "", label: "" })}
                renderItem={(it, upd) => (
                  <div className="grid sm:grid-cols-3 gap-3">
                    <TextField label="Number" type="number" value={it.value} onChange={(v) => upd({ ...it, value: Number(v) || 0 })} />
                    <TextField label="Suffix (+, MT, etc)" value={it.suffix} onChange={(v) => upd({ ...it, suffix: v })} />
                    <TextField label="Label" value={it.label} onChange={(v) => upd({ ...it, label: v })} />
                  </div>
                )} />
            </Section>

            <Section title="Verticals (4 service cards under hero)">
              <ListEditor label="Verticals" items={C.verticals || []} onChange={set("verticals")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextArea label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
                  </div>
                )} />
            </Section>

            <Section title="About Page">
              <TextField label="Eyebrow" value={C.about.eyebrow} onChange={set("about.eyebrow")} />
              <TextField label="Title" value={C.about.title} onChange={set("about.title")} />
              <TextArea label="Sub" value={C.about.sub} onChange={set("about.sub")} />
              <ImageField label="About Hero Image" value={C.about_hero_image} onChange={set("about_hero_image")} token={token} />
              <TextField label="Story Title" value={C.about.story_title} onChange={set("about.story_title")} />
              <ListEditor label="Story Paragraphs" items={C.about.story_paragraphs || []} onChange={set("about.story_paragraphs")}
                newItem={() => ""}
                renderItem={(it, upd) => <TextArea label="Paragraph" value={it} onChange={upd} rows={3} />} />
              <TextArea label="Quote" value={C.about.quote} onChange={set("about.quote")} rows={2} />
              <ListEditor label="Values (4 cards)" items={C.about.values || []} onChange={set("about.values")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextArea label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
                  </div>
                )} />
            </Section>

            <Section title="Leadership (Syed Ahamed Kabeer)">
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Name" value={C.leadership.name} onChange={set("leadership.name")} />
                <TextField label="Designation" value={C.leadership.designation} onChange={set("leadership.designation")} />
                <TextField label="Eyebrow" value={C.leadership.eyebrow} onChange={set("leadership.eyebrow")} />
                <TextField label="Title (heading)" value={C.leadership.title} onChange={set("leadership.title")} />
              </div>
              <ImageField label="Leader Photo" value={C.leadership.image} onChange={set("leadership.image")} token={token} />
              <TextArea label="Bio / Body" value={C.leadership.body} onChange={set("leadership.body")} rows={4} />
              <ListEditor label="Leadership Points (4 items)" items={C.leadership.points || []} onChange={set("leadership.points")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextArea label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
                  </div>
                )} />
            </Section>

            <Section title="Founder / Legacy (your father — optional second portrait)">
              <p className="text-xs text-[#6B7280]">
                Toggle &ldquo;Show on site&rdquo; once you&apos;ve filled in the details. The Founder section appears on Home and About pages.
              </p>
              <label className="flex items-center gap-3 text-sm font-semibold">
                <input type="checkbox" checked={!!C.founder?.enabled} onChange={(e) => set("founder.enabled")(e.target.checked)} />
                Show Founder section on site
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Founder Name" value={C.founder?.name} onChange={set("founder.name")} />
                <TextField label="Designation / Role" value={C.founder?.designation} onChange={set("founder.designation")} />
                <TextField label="Eyebrow Label" value={C.founder?.eyebrow} onChange={set("founder.eyebrow")} />
                <TextField label="Years Active (e.g. 1986 — 2010)" value={C.founder?.years_active} onChange={set("founder.years_active")} />
              </div>
              <TextField label="Section Title (heading)" value={C.founder?.title} onChange={set("founder.title")} />
              <ImageField label="Founder Photo" value={C.founder?.image} onChange={set("founder.image")} token={token} />
              <TextArea label="Biography / Tribute" value={C.founder?.body} onChange={set("founder.body")} rows={5} />
              <ListEditor label="Highlight Points (cards beside the bio)" items={C.founder?.points || []} onChange={set("founder.points")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextArea label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
                  </div>
                )} />
            </Section>

            <Section title="Manufacturing (Star Aluminium & Alloys)">
              <TextField label="Eyebrow" value={C.manufacturing.eyebrow} onChange={set("manufacturing.eyebrow")} />
              <TextField label="Title" value={C.manufacturing.title} onChange={set("manufacturing.title")} />
              <TextArea label="Sub" value={C.manufacturing.sub} onChange={set("manufacturing.sub")} rows={3} />
              <div className="grid sm:grid-cols-3 gap-4">
                <TextField label="Capacity (MT)" type="number" value={C.manufacturing.capacity_mt} onChange={(v) => set("manufacturing.capacity_mt")(Number(v) || 0)} />
                <TextField label="Partners" type="number" value={C.manufacturing.partners_count} onChange={(v) => set("manufacturing.partners_count")(Number(v) || 0)} />
                <TextField label="QC %" type="number" value={C.manufacturing.qc_percent} onChange={(v) => set("manufacturing.qc_percent")(Number(v) || 0)} />
              </div>
              <ListEditor label="Visual Gallery" items={C.manufacturing.visuals || []} onChange={set("manufacturing.visuals")}
                newItem={() => ({ src: "", label: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Caption" value={it.label} onChange={(v) => upd({ ...it, label: v })} />
                    <ImageField label="Image" value={it.src} onChange={(v) => upd({ ...it, src: v })} token={token} />
                  </div>
                )} />
              <TextField label='"Why this matters" Title' value={C.manufacturing.why_title} onChange={set("manufacturing.why_title")} />
              <TextArea label='"Why this matters" Body' value={C.manufacturing.why_body} onChange={set("manufacturing.why_body")} rows={3} />
              <ListEditor label="Bullets (4)" items={C.manufacturing.bullets || []} onChange={set("manufacturing.bullets")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} />
                  </div>
                )} />
            </Section>

            <Section title="City BOND Partnership">
              <TextField label="Eyebrow" value={C.partnership.eyebrow} onChange={set("partnership.eyebrow")} />
              <TextField label="Title" value={C.partnership.title} onChange={set("partnership.title")} />
              <TextArea label="Body" value={C.partnership.body} onChange={set("partnership.body")} rows={3} />
              <ImageField label="Partnership Banner Image" value={C.partnership.image} onChange={set("partnership.image")} token={token} />
              <ListEditor label="Bullets (4 quick highlights)" items={C.partnership.bullets || []} onChange={set("partnership.bullets")}
                newItem={() => ""}
                renderItem={(it, upd) => <TextField label="Bullet" value={it} onChange={upd} />} />
              <ListEditor label="Features (3 cards)" items={C.partnership.features || []} onChange={set("partnership.features")}
                newItem={() => ({ title: "", desc: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                    <TextArea label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} rows={2} />
                  </div>
                )} />
              <ListEditor label="Gallery (3 images)" items={C.partnership.gallery || []} onChange={set("partnership.gallery")}
                newItem={() => ""}
                renderItem={(it, upd) => <ImageField label="Gallery Image" value={it} onChange={upd} token={token} />} />
            </Section>

            <Section title="CTA Strip (homepage bottom)">
              <TextField label="Title" value={C.cta_strip.title} onChange={set("cta_strip.title")} />
              <TextField label="Accent line" value={C.cta_strip.accent} onChange={set("cta_strip.accent")} />
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Primary Button" value={C.cta_strip.primary} onChange={set("cta_strip.primary")} />
                <TextField label="Secondary Button" value={C.cta_strip.secondary} onChange={set("cta_strip.secondary")} />
              </div>
            </Section>

            <Section title="Contact Page Heading">
              <TextField label="Eyebrow" value={C.contact.eyebrow} onChange={set("contact.eyebrow")} />
              <TextField label="Title" value={C.contact.title} onChange={set("contact.title")} />
              <TextArea label="Sub" value={C.contact.sub} onChange={set("contact.sub")} rows={2} />
            </Section>

            <Section title="Footer">
              <TextArea label="Footer Note" value={C.footer.note} onChange={set("footer.note")} rows={3} />
              <TextField label="Motto Line" value={C.footer.motto} onChange={set("footer.motto")} />
            </Section>

            <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-[#E5E7EB]">
              <button onClick={() => { if (window.confirm("Reset all content to defaults?")) setDraft(DEFAULT_CONTENT); }} className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A] inline-flex items-center gap-2">
                <RefreshCw className="w-3 h-3" /> Reset Draft to Defaults
              </button>
              <button onClick={saveContent} disabled={saving} className="btn-gold px-6 py-3 text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save All Content"}
              </button>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {tab === "products" && (
          <div className="space-y-4">
            <Section title="Products Catalogue" defaultOpen>
              <ListEditor label="Products" items={prodDraft} onChange={setProdDraft}
                newItem={() => ({ id: `p${Date.now()}`, name: "New Product", category: "Aluminium Sections", description: "", image: "", features: [] })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
                      <TextField label="Category" value={it.category} onChange={(v) => upd({ ...it, category: v })} />
                    </div>
                    <TextArea label="Description" value={it.description} onChange={(v) => upd({ ...it, description: v })} rows={2} />
                    <ImageField label="Image" value={it.image} onChange={(v) => upd({ ...it, image: v })} token={token} />
                    <TextField label="Features (comma separated)" value={(it.features || []).join(", ")} onChange={(v) => upd({ ...it, features: v.split(",").map(s => s.trim()).filter(Boolean) })} />
                  </div>
                )} />
            </Section>
            <div className="flex justify-end gap-3">
              <button onClick={() => setProdDraft(DEFAULT_PRODUCTS)} className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A]">Reset to Defaults</button>
              <button onClick={() => saveList("products", prodDraft, setProdDraft)} disabled={saving} className="btn-gold px-6 py-3 text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Save Products
              </button>
            </div>
          </div>
        )}

        {/* REVIEWS TAB */}
        {tab === "reviews" && (
          <div className="space-y-4">
            <Section title="Customer Reviews" defaultOpen>
              <ListEditor label="Reviews" items={revDraft} onChange={setRevDraft}
                newItem={() => ({ id: `r${Date.now()}`, name: "", rating: 5, relative_time: "Today", text: "", avatar_initials: "", profession: "" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v, avatar_initials: (v || "").split(" ").map(s => s[0] || "").join("").toUpperCase().slice(0, 2) })} />
                      <TextField label="Profession" value={it.profession} onChange={(v) => upd({ ...it, profession: v })} />
                      <TextField label="Rating (1-5)" type="number" value={it.rating} onChange={(v) => upd({ ...it, rating: Math.max(1, Math.min(5, Number(v) || 5)) })} />
                      <TextField label="Relative Time (e.g. 2 weeks ago)" value={it.relative_time} onChange={(v) => upd({ ...it, relative_time: v })} />
                    </div>
                    <TextField label="Avatar Initials" value={it.avatar_initials} onChange={(v) => upd({ ...it, avatar_initials: v })} />
                    <TextArea label="Review Text" value={it.text} onChange={(v) => upd({ ...it, text: v })} rows={3} />
                  </div>
                )} />
            </Section>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRevDraft(DEFAULT_REVIEWS)} className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A]">Reset to Defaults</button>
              <button onClick={() => saveList("reviews", revDraft, setRevDraft)} disabled={saving} className="btn-gold px-6 py-3 text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Save Reviews
              </button>
            </div>
          </div>
        )}

        {/* PROJECTS TAB */}
        {tab === "projects" && (
          <div className="space-y-4">
            <Section title="Projects Showcase" defaultOpen>
              <ListEditor label="Projects" items={projDraft} onChange={setProjDraft}
                newItem={() => ({ id: `j${Date.now()}`, title: "", city: "", image: "", tag: "Commercial" })}
                renderItem={(it, upd) => (
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
                      <TextField label="City" value={it.city} onChange={(v) => upd({ ...it, city: v })} />
                      <TextField label="Tag (Commercial/Residential/Interior/Industrial)" value={it.tag} onChange={(v) => upd({ ...it, tag: v })} />
                    </div>
                    <ImageField label="Image" value={it.image} onChange={(v) => upd({ ...it, image: v })} token={token} />
                  </div>
                )} />
            </Section>
            <div className="flex justify-end gap-3">
              <button onClick={() => setProjDraft(DEFAULT_PROJECTS)} className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A]">Reset to Defaults</button>
              <button onClick={() => saveList("projects", projDraft, setProjDraft)} disabled={saving} className="btn-gold px-6 py-3 text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                <Save className="w-4 h-4" /> Save Projects
              </button>
            </div>
          </div>
        )}

        {/* ENQUIRIES TAB */}
        {tab === "enquiries" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Enquiry Inbox ({enquiries.length})</h2>
              <button onClick={refreshEnquiries} className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#E5E7EB] hover:border-[#1A1A1A] inline-flex items-center gap-2">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
            {enquiries.length === 0 && <p className="text-[#6B7280]">No enquiries yet.</p>}
            <div className="space-y-3">
              {enquiries.map((e) => (
                <article key={e.id} data-testid={`enquiry-${e.id}`} className="bg-white border border-[#E5E7EB] p-5 grid md:grid-cols-5 gap-4">
                  <div>
                    <div className="font-display text-base">{e.name}</div>
                    <div className="text-xs text-[#6B7280]">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-[#4B5563]">
                    <div>{e.email}</div>
                    <div>{e.phone}</div>
                    {e.company && <div className="text-xs text-[#6B7280] mt-1">{e.company}</div>}
                  </div>
                  <div className="md:col-span-2">
                    {e.subject && <div className="font-semibold text-sm">{e.subject}</div>}
                    <p className="text-sm text-[#4B5563] mt-1">{e.message}</p>
                  </div>
                  <div className="text-right">
                    <button onClick={() => deleteEnquiry(e.id)} className="text-xs uppercase tracking-widest text-red-600 hover:underline">Delete</button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
        {/* ANALYTICS TAB */}
        {tab === "analytics" && <AnalyticsPanel token={token} />}
      </div>
    </div>
  );
}
